import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ArrowLeft, GripVertical, Plus, X, Loader2, Volume2, VolumeX,
  Clock, Globe, Zap, ChevronDown, ChevronUp, RefreshCw,
  Radio, Send, Check, Pencil, Plane, Timer, Mic2, Megaphone,
  UserCheck, Rocket, Pause, Play, XCircle, CheckCircle, RotateCcw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { gateQueueAPI, gateSessionsAPI, templatesAPI } from '../../services/api';
import { useSupervisorSocket, useGateSocket } from '../../hooks/useGateSocket';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/UI/StatusBadge';
import { isEnglishOnly } from '../../utils/languageLabel';
import { formatOffset } from '../../utils/offsetLabel';
import { sessionProgress } from '../../utils/sessionProgress';
import { knownPlaceholderValues, isAirportTemplate } from '../../utils/templatePrefill';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS = {
  pending:    { label: 'Pending',    tone: 'neutral' },
  generating: { label: 'Generating', tone: 'info' },
  ready:      { label: 'Ready',      tone: 'success' },
  playing:    { label: 'Playing',    tone: 'success', pulse: true },
  interrupted:{ label: 'Interrupted (paused)', tone: 'danger' },
  played:     { label: 'Played',     tone: 'neutral' },
  skipped:    { label: 'Skipped',    tone: 'yellow' },
  cancelled:  { label: 'Cancelled',  tone: 'danger' },
};

// ── Announcement type config ──────────────────────────────────────────────────
// border/dot stay raw hex classes — the left card-edge stripe and history dot
// aren't something StatusBadge renders; tone is what feeds the badge itself.

const TYPE_CONFIG = {
  pre_boarding:      { label: 'Pre-Boarding',      tone: 'info',    border: 'border-l-blue-400',    dot: 'bg-blue-400' },
  boarding_call:     { label: 'Boarding Call',      tone: 'success', border: 'border-l-emerald-400', dot: 'bg-emerald-400' },
  boarding_reminder: { label: 'Boarding Reminder',  tone: 'yellow',  border: 'border-l-amber-400',   dot: 'bg-amber-400' },
  first_boarding:    { label: 'First Boarding',     tone: 'teal',    border: 'border-l-teal-400',    dot: 'bg-teal-400' },
  final_boarding:    { label: 'Final Boarding',     tone: 'orange',  border: 'border-l-orange-400',  dot: 'bg-orange-400' },
  gate_closing:      { label: 'Gate Closing',       tone: 'danger',  border: 'border-l-red-400',     dot: 'bg-red-400' },
  gate_closed:       { label: 'Gate Closed',        tone: 'danger',  border: 'border-l-red-600',     dot: 'bg-red-600' },
  adhoc:             { label: 'Ad-hoc',             tone: 'purple',  border: 'border-l-purple-400',  dot: 'bg-purple-400' },
  custom:            { label: 'Custom',             tone: 'purple',  border: 'border-l-fuchsia-400', dot: 'bg-fuchsia-400' },
};

// label: a custom rule's admin-typed type name (type_label), shown instead of the generic one
function typeConf(t, label) {
  const conf = TYPE_CONFIG[t] || { label: t?.replace(/_/g,' ') || '—', tone: 'neutral', border: 'border-l-slate-300', dot: 'bg-slate-300' };
  return label ? { ...conf, label } : conf;
}

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Queue times keep their seconds — offsets have 1 s precision, so "2:21" could be 2:21:13
function fmtTimeSec(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function toTimeInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return [d.getHours(), d.getMinutes(), d.getSeconds()].map(n => String(n).padStart(2, '0')).join(':');
}

function timeInputToDate(timeStr, referenceIso) {
  const ref = referenceIso ? new Date(referenceIso) : new Date();
  const [h, m, sec = 0] = timeStr.split(':').map(Number);
  const d = new Date(ref);
  d.setHours(h, m, sec, 0);
  return d;
}

function fmtDepCountdown(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff <= 0) return 'Departed';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function timeUntilScheduled(iso) {
  const diff = new Date(iso) - Date.now();
  if (diff <= 0) return { label: 'overdue', urgent: true };
  const m = Math.floor(diff / 60000);
  if (m < 2) return { label: 'imminent', urgent: true };
  return { label: `in ${m}m`, urgent: m < 10 };
}

function timeDeltaLabel(original, current) {
  if (!original) return null;
  const diffSec = Math.round((new Date(current) - new Date(original)) / 1000);
  if (diffSec === 0) return null;
  return `${diffSec > 0 ? '+' : '−'}${formatOffset(Math.abs(diffSec) / 60)}`;
}

// ── Inline time editor ────────────────────────────────────────────────────────

function TimeEditor({ ann, gateId, onSaved, startsAt }) {
  // Next in line while the gate is in its pause between announcements: tick a
  // live "starts in Ns" instead of showing "overdue"/"imminent"
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!startsAt) return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [startsAt]);
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(toTimeInput(ann.scheduled_time));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setValue(toTimeInput(ann.scheduled_time)); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open, ann.scheduled_time]);

  async function save() {
    const newDate = timeInputToDate(value, ann.scheduled_time);
    if (isNaN(newDate.getTime())) { toast.error('Invalid time'); return; }
    setSaving(true);
    try {
      await gateQueueAPI.reschedule(gateId, ann.id, newDate.toISOString());
      toast.success('Time updated');
      setOpen(false);
      onSaved();
    } catch (err) { if (!err._toasted) toast.error('Failed to update time'); }
    finally { setSaving(false); }
  }

  function handleKey(e) {
    if (e.key === 'Enter') save();
    if (e.key === 'Escape') setOpen(false);
  }

  const delta = timeDeltaLabel(ann.original_scheduled_time, ann.scheduled_time);
  const pauseLeft = startsAt ? Math.ceil((startsAt - Date.now()) / 1000) : 0;
  const { label: countdownLabel, urgent } = pauseLeft > 0 && new Date(ann.scheduled_time).getTime() < startsAt
    ? { label: `starts in ${pauseLeft}s`, urgent: false }
    : timeUntilScheduled(ann.scheduled_time);

  if (open) {
    return (
      <div className="flex items-center gap-1.5 ml-auto flex-shrink-0">
        <input ref={inputRef} type="time" step={1} value={value}
          onChange={e => setValue(e.target.value)} onKeyDown={handleKey}
          className="border border-blue-400 rounded-lg px-2 py-1 text-sm font-mono tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-400 w-[9.5rem] min-w-[9.5rem] bg-white"
        />
        <button onClick={save} disabled={saving}
          className="p-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
        </button>
        <button onClick={() => setOpen(false)}
          className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <X size={11} />
        </button>
      </div>
    );
  }

  return (
    <button onClick={() => setOpen(true)}
      className="flex items-center gap-1.5 ml-auto flex-shrink-0 group/timebtn rounded-lg px-2 py-1 hover:bg-slate-50 transition-colors"
      title="Click to adjust time"
    >
      <div className="text-right">
        <div className="flex items-center gap-1 justify-end">
          <Clock size={10} className="text-slate-400" />
          <span className="font-mono text-xs text-slate-600 font-medium">{fmtTimeSec(ann.scheduled_time)}</span>
          {delta && (
            <span className={`text-xs font-bold ${delta.startsWith('+') ? 'text-orange-500' : 'text-blue-500'}`}>{delta}</span>
          )}
          <Pencil size={9} className="opacity-0 group-hover/timebtn:opacity-100 text-blue-500 transition-opacity" />
        </div>
        <span className={`text-[10px] font-medium ${urgent ? 'text-orange-500' : 'text-slate-400'}`}>{countdownLabel}</span>
      </div>
    </button>
  );
}

// ── Shift-all bar ─────────────────────────────────────────────────────────────

function ShiftBar({ gateId, hasItems, onShifted }) {
  const [shifting, setShifting] = useState(false);

  async function shift(delta) {
    setShifting(true);
    try {
      await gateQueueAPI.shift(gateId, delta);
      toast.success(`All remaining announcements shifted ${delta > 0 ? '+' : ''}${delta}m`);
      onShifted();
    } catch (err) { if (!err._toasted) toast.error('Failed to shift schedule'); }
    finally { setShifting(false); }
  }

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-2xl mb-4 transition-opacity ${!hasItems ? 'opacity-40 pointer-events-none' : ''}`}>
      <div className="flex items-center gap-1.5 flex-shrink-0 w-full sm:w-auto">
        {shifting ? <Loader2 size={13} className="animate-spin text-amber-500" /> : <Timer size={13} className="text-amber-600" />}
        <span className="text-xs font-bold text-amber-700">Shift all</span>
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      {/* Earlier */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Earlier</span>
        {[-15, -10, -5].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting || !hasItems}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-blue-200 text-blue-700 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50 shadow-sm">
            {d}m
          </button>
        ))}
      </div>

      <div className="hidden sm:block h-4 w-px bg-amber-200" />

      {/* Later */}
      <div className="flex items-center gap-1">
        <span className="text-[10px] text-amber-500 font-semibold uppercase tracking-wide mr-0.5">Later</span>
        {[5, 10, 15].map(d => (
          <button key={d} onClick={() => shift(d)} disabled={shifting || !hasItems}
            className="px-2.5 py-1 rounded-xl text-xs font-bold border border-orange-200 text-orange-700 bg-white hover:bg-orange-50 transition-colors disabled:opacity-50 shadow-sm">
            +{d}m
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Sortable announcement card ────────────────────────────────────────────────

function SortableAnnCard({ ann, index, gateId, onCancel, onSaved, insertForm, onInsertAfter, canReorder, canInsert, isLast, startsAt }) {
  const draggable = ['pending', 'generating', 'ready'].includes(ann.status);
  // Reordering AND direct insertion are both supervisor/manager/admin only,
  // server-side (PATCH/POST .../queue/reorder, /insert) — gate operators can
  // still reschedule/cancel items, just not reorder or directly insert.
  const dragEnabled = draggable && canReorder;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: ann.id, disabled: !dragEnabled,
  });

  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.35 : 1 };
  const sc = STATUS[ann.status] || STATUS.pending;
  const tc = typeConf(ann.announcement_type, ann.type_label);

  return (
    <div ref={setNodeRef} style={style} className="group/card">
      <div className={`flex items-stretch bg-white border border-slate-200 rounded-2xl overflow-hidden transition-shadow
        ${isDragging ? 'shadow-2xl ring-2 ring-blue-400' : 'shadow-sm hover:shadow-md'}
        ${ann.status === 'cancelled' ? 'opacity-40' : ''}`}
      >
        {/* Left color border */}
        <div className={`w-1 flex-shrink-0 ${tc.border} border-l-4 rounded-l-2xl`} />

        {/* Drag handle — only interactive when the role is allowed to reorder */}
        <div
          {...(dragEnabled ? { ...attributes, ...listeners } : {})}
          className={`flex items-center px-2 ${dragEnabled ? 'touch-none cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400' : 'text-slate-100 cursor-default'}`}
        >
          <GripVertical size={16} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 px-3 py-3">
          {/* Top row: type badge + status + time */}
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <StatusBadge label={tc.label} tone={tc.tone} />
            <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} dot={sc.pulse} />
            {ann.status === 'generating' && <Loader2 size={10} className="animate-spin text-blue-500" />}

            {draggable ? (
              <TimeEditor ann={ann} gateId={gateId} onSaved={onSaved} startsAt={startsAt} />
            ) : (
              <span className="text-xs text-slate-400 flex items-center gap-1 ml-auto">
                <Clock size={10} /> {fmtTimeSec(ann.scheduled_time)}
              </span>
            )}
          </div>

          {/* English text */}
          {ann.english_text && (
            <p className="text-sm text-slate-700 leading-snug line-clamp-2 font-medium">{ann.english_text}</p>
          )}

          {/* Secondary text */}
          {ann.secondary_text && (
            <p className="text-xs text-slate-400 mt-1.5 line-clamp-1 flex items-center gap-1.5">
              <Globe size={10} className="flex-shrink-0 text-slate-300" />
              <span className="italic">{ann.secondary_text}</span>
            </p>
          )}
        </div>

        {/* Cancel button */}
        {draggable && (
          <div className="flex items-center pr-3">
            <button onClick={() => onCancel(ann.id)}
              className="p-1.5 rounded-xl text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors opacity-0 group-hover/card:opacity-100 [@media(hover:none)]:opacity-100"
              title="Remove from queue"
            >
              <X size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Insert-after slot — supervisor/manager/admin only, same as the server-side check */}
      {/* Insert-after slot — direct insert for supervisor/manager/admin, an
          approval request at this position for gate operators */}
      {draggable && canInsert && <InsertSlot annId={ann.id} form={insertForm} onOpen={onInsertAfter} label={isLast ? 'Insert at end' : 'Insert here'} />}
    </div>
  );
}

function DragCard({ ann }) {
  const tc = typeConf(ann?.announcement_type, ann?.type_label);
  const sc = STATUS[ann?.status] || STATUS.pending;
  return (
    <div className={`flex items-stretch bg-white border-2 border-blue-400 rounded-2xl overflow-hidden shadow-2xl w-80`}>
      <div className={`w-1 flex-shrink-0 ${tc.border} border-l-4`} />
      <div className="px-3 py-3 flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <StatusBadge label={tc.label} tone={tc.tone} size="sm" />
          <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} size="sm" />
          <span className="text-xs text-slate-400 ml-auto font-mono">{fmtTimeSec(ann?.scheduled_time)}</span>
        </div>
        {ann?.english_text && <p className="text-sm text-slate-700 line-clamp-2 font-medium">{ann.english_text}</p>}
      </div>
    </div>
  );
}

// ── Insert slot / form ────────────────────────────────────────────────────────

// Hover-revealed on desktop; always shown on touch screens (gate operators use
// tablets, which have no hover).
const SLOT_BTN = 'relative opacity-0 group-hover/slot:opacity-100 [@media(hover:none)]:opacity-100 flex items-center gap-1 px-3 py-0.5 rounded-full bg-white border border-blue-200 text-blue-600 text-xs font-semibold hover:bg-blue-50 shadow-sm transition-all';

function InsertSlot({ annId, form, onOpen, label = 'Insert here' }) {
  const isOpen = form?.insertAfterId === annId;
  if (isOpen) return null;
  return (
    <div className="relative my-1 flex items-center justify-center group/slot h-6">
      <div className="absolute inset-x-0 top-1/2 h-px bg-slate-100 group-hover/slot:bg-blue-100 transition-colors" />
      <button onClick={() => onOpen(annId)} className={SLOT_BTN}>
        <Plus size={11} /> {label}
      </button>
    </div>
  );
}

function TopInsertSlot({ form, onOpen }) {
  const isOpen = form?.insertAfterId === null && form !== null;
  if (isOpen) return null;
  return (
    <div className="flex items-center justify-center mb-2 group/slot h-6">
      <button onClick={() => onOpen(null)} className={SLOT_BTN}>
        <Plus size={11} /> Insert at top
      </button>
    </div>
  );
}

// Ad-hoc at a queue position — no time to pick: it plays at that point in the
// sequence. mode 'direct' inserts straight away (supervisor/manager/admin);
// mode 'request' sends an approval request for this position (gate operator —
// CR: operators can't broadcast unapproved). A template fills the text, with
// flight/gate details pre-filled from this session; the text stays editable.
function InsertForm({ insertAfterId, positionLabel, onSubmit, onClose, loading, mode = 'direct', templates = [], flightCtx }) {
  const [text, setText] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [values, setValues] = useState({});
  const textRef = useRef(null);
  useEffect(() => { textRef.current?.focus(); }, []);

  const template = templates.find(t => String(t.id) === String(templateId));
  const placeholders = template?.placeholders || [];

  function fill(tpl, vals) {
    return (tpl?.template_text || '').replace(/\{([^}]+)\}/g, (m, name) => (vals[name]?.trim() ? vals[name].trim() : m));
  }

  function chooseTemplate(id) {
    setTemplateId(id);
    const tpl = templates.find(t => String(t.id) === String(id));
    if (!tpl) { setValues({}); return; }
    const known = knownPlaceholderValues(tpl.placeholders || [], flightCtx || {});
    setValues(known);
    setText(fill(tpl, known));
  }

  function setValue(name, v) {
    const next = { ...values, [name]: v };
    setValues(next);
    setText(fill(template, next));
  }

  const unfilled = [...new Set((text.match(/\{([^}]+)\}/g) || []).map(m => m.slice(1, -1)))];

  function handleSubmit(e) {
    e.preventDefault();
    if (!text.trim() || unfilled.length) return;
    onSubmit(text.trim(), insertAfterId);
  }

  const isRequest = mode === 'request';
  return (
    <div className="my-2 bg-purple-50 border border-purple-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-purple-700 mb-3 flex items-center gap-1.5 uppercase tracking-wide">
        <Zap size={12} /> {isRequest ? 'Request ad-hoc announcement' : 'Insert ad-hoc announcement'}
        <span className="normal-case font-semibold text-purple-500 tracking-normal">· {positionLabel}</span>
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {templates.length > 0 && (
          <select value={templateId} onChange={e => chooseTemplate(e.target.value)}
            className="w-full border border-purple-200 rounded-xl px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400">
            <option value="">Start from a template (optional)…</option>
            {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        )}
        {placeholders.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {placeholders.map(p => (
              <label key={p} className="flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-slate-500">{p}</span>
                <input value={values[p] || ''} onChange={e => setValue(p, e.target.value)}
                  className={`border rounded-lg px-2.5 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-purple-400 ${values[p]?.trim() ? 'border-slate-200' : 'border-amber-300'}`} />
              </label>
            ))}
          </div>
        )}
        <textarea ref={textRef} value={text} onChange={e => setText(e.target.value)}
          placeholder="Type the announcement text in English…"
          rows={3} maxLength={1000}
          className="w-full border border-purple-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none bg-white shadow-sm"
        />
        {unfilled.length > 0 && (
          <p className="text-xs font-medium text-amber-700">Fill in: {unfilled.map(u => `{${u}}`).join(', ')}</p>
        )}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-xs text-slate-400">
            {text.length}/1000 · {isRequest ? 'Plays at this position once a supervisor or manager approves' : 'Translation & audio auto-generated'}
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} disabled={loading}
              className="px-3 py-1.5 rounded-xl text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading || !text.trim() || unfilled.length > 0}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-xs font-bold bg-purple-700 text-white hover:bg-purple-800 disabled:opacity-50 transition-colors shadow-sm">
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              {loading ? (isRequest ? 'Sending…' : 'Adding…') : (isRequest ? 'Send for approval' : 'Add to queue')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// A request still awaiting approval, shown where it will land once approved —
// dashed so it reads as "not in the queue yet".
function PendingRequestCard({ req, reviewPath, compact, passed }) {
  const where = req.timing_type === 'sequence'
    ? null
    : req.timing_type === 'clock' && req.scheduled_at
      ? `at ${fmtTime(req.scheduled_at)}`
      : req.timing_type === 'relative'
        ? (Number(req.relative_minutes) ? `${req.relative_minutes} min after approval` : 'as soon as approved')
        : null;
  return (
    <div className={`${compact ? '' : 'my-1'} rounded-2xl border-2 border-dashed border-purple-300 bg-purple-50/60 px-4 py-3`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
          <Loader2 size={11} className="animate-spin" /> Awaiting approval
        </span>
        <span className="text-xs text-purple-600 font-medium">
          {where ? `Plays ${where}` : passed ? 'Its spot has passed — plays next once approved' : 'Plays at this position once approved'}
        </span>
        {req.requester?.name && <span className="text-xs text-slate-400">· requested by {req.requester.name}</span>}
        {reviewPath && (
          <Link to={reviewPath} className="ml-auto text-xs font-bold text-purple-700 hover:text-purple-900">Review →</Link>
        )}
      </div>
      {req.english_text && <p className="text-sm text-slate-700 mt-1.5 line-clamp-2">{req.english_text}</p>}
    </div>
  );
}

// ── Progress bar ──────────────────────────────────────────────────────────────

// Includes the announcement playing right now — leaving it out made the total
// drop by one whenever something was playing (0/6 ↔ 0/5).
function QueueProgress({ playing, upcoming, history }) {
  const { played: done, total } = sessionProgress([...(playing || []), ...(upcoming || []), ...(history || [])]);
  if (total === 0) return null;
  const pct = Math.round((done / total) * 100);

  return (
    <div className="mb-4 px-4 py-3 bg-white rounded-2xl border border-slate-200 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Session Progress</span>
        <span className="text-xs font-bold text-slate-600">{done} / {total} played</span>
      </div>
      <div className="w-full bg-slate-100 rounded-full h-2">
        <div
          className="bg-emerald-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── Session controls ──────────────────────────────────────────────────────────
// Acknowledge / Initiate before the programme starts (CR 5.1/5.2: both shown
// together, Initiate disabled until Acknowledge succeeds); Emergency Pause /
// Resume and Cancel while it runs; Request shortcuts for gate operators (CR 4.3:
// the gate auto-attaches).

function SessionControls({ session, loading, canCancel, showRequestLinks, onAcknowledge, onInitiate, onPauseResume, onCancel, onReopen }) {
  const preInitiate = ['pending', 'acknowledged'].includes(session.status);
  const running = ['active', 'paused'].includes(session.status);
  const acknowledged = session.status === 'acknowledged';
  const btn = 'flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-semibold text-sm transition-colors disabled:cursor-not-allowed';

  // Finished flight: nothing left to control, and no further requests for it
  if (['completed', 'cancelled'].includes(session.status)) {
    const cancelled = session.status === 'cancelled';
    return (
      <div className={`mb-4 flex items-center gap-2.5 px-4 py-3 rounded-xl border text-sm ${
        cancelled ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
        {cancelled ? <XCircle size={17} className="flex-shrink-0" /> : <CheckCircle size={17} className="flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <p className="font-bold">{cancelled ? 'Session cancelled' : 'Programme completed'}</p>
          <p className="text-xs opacity-80">
            {cancelled ? 'No further announcements will play for this flight.' : 'All announcements for this flight have finished. Nothing more to do here.'}
          </p>
        </div>
        {onReopen && (
          <button onClick={onReopen} disabled={loading}
            title="Let a gate operator run this flight's announcements again"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors flex-shrink-0 disabled:opacity-50">
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
            Reopen
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="mb-4 space-y-2">
      {preInitiate && (
        <div className="flex gap-2">
          <button onClick={onAcknowledge} disabled={loading || acknowledged}
            className={`${btn} ${acknowledged ? 'bg-slate-100 text-slate-400' : 'bg-slate-700 hover:bg-slate-800 text-white disabled:opacity-50'}`}>
            {loading && !acknowledged ? <Loader2 size={15} className="animate-spin" /> : acknowledged ? <CheckCircle size={15} /> : <UserCheck size={15} />}
            {acknowledged ? 'Acknowledged' : 'Acknowledge'}
          </button>
          <button onClick={onInitiate} disabled={loading || !acknowledged}
            className={`${btn} ${acknowledged ? 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50' : 'bg-slate-100 text-slate-400'}`}>
            {loading && acknowledged ? <Loader2 size={15} className="animate-spin" /> : <Rocket size={15} />}
            Initiate
          </button>
        </div>
      )}

      {running && (
        <div className="flex gap-2">
          <button onClick={onPauseResume} disabled={loading}
            className={`${btn} text-white disabled:opacity-60 ${session.status === 'paused' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-amber-500 hover:bg-amber-600'}`}>
            {loading ? <Loader2 size={15} className="animate-spin" /> : session.status === 'paused' ? <Play size={15} /> : <Pause size={15} />}
            {session.status === 'paused' ? 'Resume' : 'Emergency Pause'}
          </button>
          {canCancel && (
            <button onClick={onCancel} disabled={loading}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl font-semibold text-sm bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 disabled:opacity-60">
              <XCircle size={15} /> Cancel
            </button>
          )}
        </div>
      )}

      {showRequestLinks && session.gate_id && (
        <div className="flex gap-2">
          <Link to={`/operator/request-template?gate_id=${session.gate_id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl font-semibold text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border border-blue-200 transition-colors">
            <Megaphone size={13} /> Request Template
          </Link>
          <Link to={`/operator/adhoc?gate_id=${session.gate_id}`}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl font-semibold text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 transition-colors">
            <Mic2 size={13} /> Request Ad-Hoc
          </Link>
        </div>
      )}
    </div>
  );
}

// Before Initiate, no announcements exist yet — show the programme that will run,
// with times relative to Initiate since there's nothing to anchor them to yet.
function ProgrammePreview({ rules, ruleSet }) {
  if (rules.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200 shadow-sm">
        <Radio size={40} className="text-slate-200 mx-auto mb-3" />
        <p className="text-slate-500 font-semibold">No announcement programme configured</p>
      </div>
    );
  }
  return (
    <div>
      <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide mb-3 flex items-center gap-2 flex-wrap">
        Announcement Programme
        {ruleSet && (
          <span className="normal-case tracking-normal text-xs font-bold px-2 py-0.5 rounded-lg bg-blue-50 text-blue-700 border border-blue-200">
            {ruleSet.airline_code ? `${ruleSet.airline_name} rules` : 'Global rules'}
          </span>
        )}
      </h2>
      <div className="space-y-2">
        {rules.map(rule => {
          const tc = typeConf(rule.announcement_type, rule.type_label);
          return (
            <div key={rule.id} className={`flex items-center justify-between gap-3 bg-white rounded-xl border border-slate-200 border-l-4 ${tc.border} px-4 py-3 shadow-sm`}>
              <span className="text-sm font-semibold text-slate-800">
                {tc.label}
                {rule.repeat_count > 1 && <span className="text-slate-400 font-normal"> ×{rule.repeat_count}</span>}
              </span>
              <span className="text-xs font-mono text-slate-500">
                {Number(rule.offset_minutes) === 0 ? 'At initiation' : `T+${formatOffset(rule.offset_minutes)}`}
              </span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-slate-400 mt-3">
        Times are relative to Initiate. Exact playback times are set once the programme is initiated, and can then be edited here.
      </p>
    </div>
  );
}

export default function AdminGateQueuePage() {
  const { gateId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canReorder = ['admin', 'supervisor', 'manager'].includes(user?.role);
  // Direct queue insertion is the same supervisor/manager/admin-only action
  // server-side (POST .../queue/insert rejects gate_operator with a 403) — the
  // button/form must be hidden for operators too, not just fail silently after
  // they've already filled it in. Operators use the "Request Ad-Hoc" approval
  // flow instead (AdHocPage.jsx).
  // Supervisor/manager/admin insert directly; gate operators get the same
  // controls but each insert becomes an approval request for that position.
  const canInsertDirect = canReorder;
  const canInsert = canInsertDirect || user?.role === 'gate_operator';
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (!canInsert) return;
    templatesAPI.getAll({ isActive: true, limit: 100 })
      .then(res => setTemplates((res.data.data?.templates || res.data.data || []).filter(isAirportTemplate)))
      .catch(() => {});
  }, [canInsert]);

  // ?session= pins the exact session opened from a session list (a gate can have
  // several queued up across the day); without it the API picks the running one.
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionParam = searchParams.get('session');
  const isOperator = user?.role === 'gate_operator';
  const canCancelSession = canReorder;
  const [controlLoading, setControlLoading] = useState(false);

  const [queueData, setQueueData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [activeAnn, setActiveAnn] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [insertForm, setInsertForm] = useState(null);
  const [inserting, setInserting] = useState(false);
  const [countdown, setCountdown] = useState('');

  // Live airport-announcement audio playback
  const [nowPlaying, setNowPlaying] = useState(null); // { title, text }
  const audioRef = useRef(new Audio());

  // Sound on this page is a monitoring convenience — the gate's own device is
  // the real speaker. Off by default for gate operators (their screen shouldn't
  // be a second PA), on for supervisors/admins as before; each user's choice is
  // remembered in this browser. Muted = don't play AND don't report 'played',
  // so the gate device remains the one that marks announcements finished.
  const soundKey = `queueSound:${user?.id ?? 'anon'}`;
  const [soundOn, setSoundOn] = useState(() => {
    try {
      const saved = localStorage.getItem(soundKey);
      if (saved !== null) return saved === 'on';
    } catch { /* storage unavailable — fall back to the role default */ }
    return user?.role !== 'gate_operator';
  });
  const soundOnRef = useRef(soundOn);
  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  function toggleSound() {
    const next = !soundOn;
    setSoundOn(next);
    try { localStorage.setItem(soundKey, next ? 'on' : 'off'); } catch { /* not persisted */ }
    if (!next) {
      // Stop local playback; leave reporting to the gate device
      audioRef.current.pause();
      audioRef.current.onended = null;
      currentPlayAnnIdRef.current = null;
      setNowPlaying(null);
    }
  }
  const currentPlayAnnIdRef = useRef(null); // tracks the active PLAY (gate queue) ann ID

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const res = await gateQueueAPI.getQueue(gateId, sessionParam);
      const data = res.data.data;
      setQueueData(data);
      setUpcoming(data.upcoming || []);
    } catch (err) {
      // The API's own message (e.g. 403 "belongs to another operator") is already
      // toasted by api.js; only add a generic one otherwise, and never stack it.
      if (!err._toasted) toast.error('Failed to load queue', { id: 'queue-load' });
    }
    finally { setLoading(false); setRefreshing(false); }
  }, [gateId, sessionParam]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, [load]);

  // Live departure countdown
  useEffect(() => {
    if (!queueData?.session?.departure_time) return;
    const tick = () => setCountdown(fmtDepCountdown(queueData.session.departure_time));
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [queueData?.session?.departure_time]);

  // Opened without ?session= — pin the session first shown, so later refreshes
  // stay on this flight even after it completes (never swap to the gate's next one)
  useEffect(() => {
    const id = queueData?.session?.id;
    if (sessionParam || !id) return;
    const next = new URLSearchParams(searchParams);
    next.set('session', String(id));
    setSearchParams(next, { replace: true });
  }, [queueData?.session?.id, sessionParam]); // eslint-disable-line react-hooks/exhaustive-deps

  // A finished session is all history — show it open
  useEffect(() => {
    if (['completed', 'cancelled'].includes(queueData?.session?.status)) setShowHistory(true);
  }, [queueData?.session?.status]);

  // Remember which session this operator is working — Ad-Hoc, Request Template and
  // Announce reuse it via useMyActiveGateSession, Acknowledge not required first.
  useEffect(() => {
    const s = queueData?.session;
    if (!s || !isOperator) return;
    localStorage.setItem('currentGateSessionId', String(s.id));
  }, [queueData?.session?.id, isOperator]);

  // Join gate room — receive and play audio commands for monitoring/testing
  const { emit: gateEmit } = useGateSocket({
    gateId,
    onEvent: (evt, data) => {
      if (evt === 'gate:command') {
        const { command, payload } = data || {};
        const { audioUrl, englishText, title, announcementId } = payload || {};

        if ((command === 'PLAY' || command === 'PLAY_ADHOC') && audioUrl) {
          if (!soundOnRef.current) { load(true); return; } // muted: refresh status only
          const audio = audioRef.current;

          // If a gate-queue PLAY is being interrupted by PLAY_ADHOC (airport ann),
          // emit announcement:played for the interrupted one so it doesn't stay stuck.
          if (command === 'PLAY_ADHOC' && currentPlayAnnIdRef.current) {
            gateEmit('announcement:played', { announcementId: currentPlayAnnIdRef.current, gateId });
            currentPlayAnnIdRef.current = null;
          }

          audio.pause();
          const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '';
          const baseUrl = SOCKET_URL || window.location.origin;
          const fullUrl = audioUrl.startsWith('http') ? audioUrl : `${baseUrl}${audioUrl}`;
          audio.src = fullUrl;
          audio.load();
          audio.play().catch(() => {});

          if (command === 'PLAY') currentPlayAnnIdRef.current = announcementId;
          setNowPlaying({ title, text: englishText });

          audio.onended = () => {
            setNowPlaying(null);
            if (command === 'PLAY' && announcementId) {
              gateEmit('announcement:played', { announcementId, gateId });
              currentPlayAnnIdRef.current = null;
            }
            load(true);
          };
        }
      }
      if (['announcement:status', 'queue:updated', 'session:status'].includes(evt)) load(true);
      if (evt === 'request:decision' && data) {
        // Only ever sent to the requester — tell them what happened to it
        const snippet = data.text ? `"${data.text.slice(0, 60)}${data.text.length > 60 ? '…' : ''}"` : 'Your request';
        if (data.status === 'approved') toast.success(`${snippet} was approved — it's now in the queue`);
        else if (data.status === 'rejected') toast.error(`${snippet} was rejected${data.reason ? `: ${data.reason}` : ''}`, { duration: 8000 });
        else if (data.status === 'expired') toast(`${snippet} expired — it wasn't approved before the flight's session ended`, { icon: '⌛', duration: 8000 });
        load(true);
      }
    },
  });

  useSupervisorSocket({
    onEvent: (evt) => {
      if (['announcement:status', 'queue:updated', 'session:status'].includes(evt)) load(true);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function handleDragStart(event) {
    setActiveAnn(upcoming.find(a => a.id === event.active.id) || null);
  }

  async function handleDragEnd(event) {
    setActiveAnn(null);
    if (!canReorder) return; // defense in depth — per-card drag is already disabled for this role
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = upcoming.findIndex(a => a.id === active.id);
    const newIdx = upcoming.findIndex(a => a.id === over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(upcoming, oldIdx, newIdx);
    setUpcoming(reordered);
    try {
      await gateQueueAPI.reorder(gateId, reordered.map(a => a.id));
      toast.success('Queue reordered');
      load(true);
    } catch {
      setUpcoming(upcoming);
      toast.error('Failed to save order');
    }
  }

  async function handleInsert(text, insertAfterId) {
    setInserting(true);
    try {
      if (!canInsertDirect) {
        // Operator: approval request pinned to this queue position (no time needed)
        await api.post(`/gates/${gateId}/adhoc`, { text, timing_type: 'sequence', insert_after_id: insertAfterId });
        toast.success('Sent for approval — it will play at this position once approved');
        setInsertForm(null);
        load(true); // show it straight away as an "Awaiting approval" placeholder
        return;
      }
      await gateQueueAPI.insert(gateId, { text, insert_after_id: insertAfterId });
      toast.success('Ad-hoc announcement added');
      setInsertForm(null);
      load(true);
    } catch (err) { if (!err._toasted) toast.error('Failed to insert'); }
    finally { setInserting(false); }
  }

  // ── Session controls (moved here from the old sequence page) ──────────────
  // Acknowledge only records presence; Initiate starts the programme timer.
  async function sessionAction(fn, successMsg, failMsg) {
    try {
      setControlLoading(true);
      await fn();
      toast.success(successMsg);
      load(true);
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || failMsg);
    } finally {
      setControlLoading(false);
    }
  }

  const handleAcknowledge = () => sessionAction(
    () => gateSessionsAPI.acknowledge(queueData.session.id),
    'Acknowledged — press Initiate when ready to start the programme', 'Failed to acknowledge session');

  const handleInitiate = () => sessionAction(
    () => gateSessionsAPI.initiate(queueData.session.id),
    'Initiated — announcement programme is now running', 'Failed to initiate session');

  function handlePauseResume() {
    const newStatus = queueData.session.status === 'paused' ? 'active' : 'paused';
    if (newStatus === 'paused' &&
        !window.confirm('Pause all announcements for this flight? This should only be used during an operational emergency.')) {
      return;
    }
    sessionAction(() => gateSessionsAPI.updateStatus(queueData.session.id, newStatus),
      newStatus === 'paused' ? 'Paused' : 'Resumed', 'Failed to update status');
  }

  function handleCancelSession() {
    if (!confirm('Cancel all remaining announcements for this session?')) return;
    sessionAction(() => gateSessionsAPI.updateStatus(queueData.session.id, 'cancelled'),
      'Session cancelled', 'Failed to cancel session');
  }

  // Admin only — a fresh pending session for this finished flight; switch the
  // page to it so the admin can see it waiting for an operator.
  async function handleReopen() {
    const s = queueData.session;
    if (!confirm(`Reopen flight ${s.flight_number} at gate ${gate?.gate_code}? A gate operator can then enter this gate and flight again, Acknowledge and Initiate to replay the announcements. This session's history is kept.`)) return;
    try {
      setControlLoading(true);
      const res = await gateSessionsAPI.reopen(s.id);
      toast.success(`${s.flight_number} reopened — waiting for a gate operator`);
      const next = new URLSearchParams(searchParams);
      next.set('session', String(res.data.data.id));
      setSearchParams(next, { replace: true });
    } catch (err) {
      if (!err._toasted) toast.error(err.response?.data?.message || 'Failed to reopen session');
    } finally {
      setControlLoading(false);
    }
  }

  async function handleCancel(annId) {
    if (!confirm('Remove this announcement from the queue?')) return;
    try {
      await gateQueueAPI.cancel(gateId, annId);
      setUpcoming(u => u.filter(a => a.id !== annId));
      toast.success('Announcement removed');
    } catch (err) { if (!err._toasted) toast.error('Failed to remove'); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-slate-400">Loading queue…</span>
        </div>
      </div>
    );
  }

  const { gate, session, playing, history, scheduledAirportAnns } = queueData || {};
  const sessionEnded = ['completed', 'cancelled'].includes(session?.status);
  const insertMode = canInsertDirect ? 'direct' : 'request';
  const flightCtx = { gate, session };
  // Pending requests: pinned to the top / after an upcoming item, or "other"
  // (time-based, or anchored to an item that has since played).
  const pendingRequests = queueData?.pendingRequests || [];
  const upcomingIds = new Set(upcoming.map(a => a.id));
  // The queue never waits for an unapproved request: once the item it was
  // pinned after has played, it floats to the top ("plays next once approved"),
  // which is exactly where approval will insert it.
  const pendingTop = pendingRequests.filter(r => r.timing_type === 'sequence' && (r.insert_after_id == null || !upcomingIds.has(r.insert_after_id)));
  const pendingAfter = (id) => pendingRequests.filter(r => r.timing_type === 'sequence' && r.insert_after_id === id);
  const pendingOther = pendingRequests.filter(r => r.timing_type !== 'sequence');
  const reviewPath = user?.role === 'admin' ? '/admin/airport-announcements'
    : ['supervisor', 'manager'].includes(user?.role) ? '/supervisor/announcements' : null;

  const positionLabel = (afterId) => {
    if (afterId === null) return 'at the top — plays next';
    const a = upcoming.find(x => x.id === afterId);
    return a ? `after ${typeConf(a.announcement_type, a.type_label).label} (${fmtTimeSec(a.scheduled_time)})` : 'in the queue';
  };

  return (
    <div className="px-4 py-5 max-w-2xl mx-auto">

      {/* ── Live airport announcement banner ── */}
      {nowPlaying && (
        <div className="mb-4 flex items-center gap-3 bg-blue-600 text-white rounded-xl px-4 py-3 shadow-lg animate-pulse">
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Megaphone size={16} />
            <span className="text-xs font-bold uppercase tracking-wide">Now Playing</span>
          </div>
          <div className="min-w-0 flex-1">
            {nowPlaying.title && <p className="text-sm font-semibold truncate">{nowPlaying.title}</p>}
            {nowPlaying.text && <p className="text-xs text-blue-100 truncate">{nowPlaying.text}</p>}
          </div>
          <Volume2 size={16} className="flex-shrink-0 opacity-80" />
        </div>
      )}

      {/* ── Header ── (phones: back + actions on one row, flight card full width below) */}
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 mb-5">
        <button onClick={() => navigate(-1)}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-500 transition-colors flex-shrink-0">
          <ArrowLeft size={18} />
        </button>

        {/* Flight info card */}
        <div className="order-last sm:order-none w-full sm:w-auto sm:flex-1 min-w-0 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl px-4 py-3 shadow-md">
          <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-x-3 gap-y-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-0.5">
                <h1 className="text-2xl font-black text-white tracking-tight">Gate {gate?.gate_code}</h1>
                {gate?.terminal && (
                  <span className="text-xs font-bold px-2 py-0.5 rounded-lg bg-white/20 text-white/90 backdrop-blur">
                    {gate.terminal}
                  </span>
                )}
              </div>
              {session ? (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 text-white/80 text-sm">
                    <Plane size={13} className="text-white/60" />
                    <span className="font-semibold text-white">{session.flight_number}</span>
                    <span className="text-white/60">→</span>
                    <span>{session.destination}</span>
                  </div>
                  <span className="text-white/40 text-xs">·</span>
                  <span className="text-white/70 text-sm">Dep {fmtTime(session.departure_time)}</span>
                </div>
              ) : (
                <p className="text-white/50 text-sm mt-0.5">No session</p>
              )}
            </div>

            <div className="flex flex-row flex-wrap items-center sm:flex-col sm:items-end gap-1.5 sm:flex-shrink-0">
              {session && (
                <span className={`text-xs font-black px-2.5 py-1 rounded-xl ${countdown === 'Departed' ? 'bg-red-500/30 text-red-300' : 'bg-blue-500/30 text-blue-300'}`}>
                  {countdown || fmtDepCountdown(session.departure_time)}
                </span>
              )}
              {session?.secondary_language && (
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg font-semibold ${session.language_auto_detected ? 'bg-emerald-500/20 text-emerald-300' : 'bg-white/10 text-white/60'}`}>
                  <Globe size={9} />
                  {isEnglishOnly(session.secondary_language) ? 'English only' : session.secondary_language}
                  {session.language_auto_detected && <span className="text-emerald-400">auto</span>}
                </span>
              )}
              {session?.rule_set && (
                // Which announcement rule set this flight gets / got at Initiate
                <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg font-semibold bg-white/10 text-white/70"
                  title={session.rule_set.airline_code ? `${session.rule_set.airline_name} has its own announcement rules` : 'This flight uses the Global announcement rules'}>
                  Rules: {session.rule_set.airline_code ? session.rule_set.airline_name : 'Global'}
                </span>
              )}
            </div>
          </div>
        </div>

        <button onClick={toggleSound}
          aria-pressed={soundOn}
          title={soundOn ? 'Sound on — announcements play on this screen. Click to mute.' : 'Sound off — announcements play only on the gate device. Click to hear them here.'}
          className={`ml-auto sm:ml-0 flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-xs font-semibold flex-shrink-0 transition-colors border ${
            soundOn ? 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:bg-emerald-100' : 'bg-white border-slate-200 text-slate-500 hover:bg-slate-100'
          }`}>
          {soundOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          {soundOn ? 'Sound on' : 'Sound off'}
        </button>

        <button onClick={() => load(true)} disabled={refreshing}
          className="p-2 rounded-xl hover:bg-slate-100 text-slate-400 flex-shrink-0 transition-colors" title="Refresh">
          <RefreshCw size={16} className={refreshing ? 'animate-spin text-blue-500' : ''} />
        </button>
      </div>

      {/* ── Session controls ── */}
      {session && (
        <SessionControls
          session={session}
          loading={controlLoading}
          canCancel={canCancelSession}
          showRequestLinks={isOperator}
          onAcknowledge={handleAcknowledge}
          onInitiate={handleInitiate}
          onPauseResume={handlePauseResume}
          onCancel={handleCancelSession}
          onReopen={user?.role === 'admin' ? handleReopen : null}
        />
      )}

      {/* ── No session ── */}
      {!session ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-slate-200 shadow-sm">
          <Radio size={44} className="text-slate-200 mx-auto mb-3" />
          <p className="text-slate-500 font-semibold">No session at this gate</p>
          <p className="text-slate-400 text-sm mt-1">Sessions are created automatically when a flight is synced.</p>
        </div>
      ) : ['pending', 'acknowledged'].includes(session.status) ? (
        <ProgrammePreview rules={session.programme_preview || []} ruleSet={session.rule_set} />
      ) : (
        <>
          {/* ── Progress ── */}
          <QueueProgress playing={playing} upcoming={upcoming} history={history} />

          {/* ── Now Playing ── */}
          {playing && playing.length > 0 && (
            <div className="bg-emerald-600 rounded-2xl px-4 py-4 mb-4 shadow-lg shadow-emerald-100">
              <div className="flex items-center gap-2 mb-2">
                <div className="flex gap-0.5 items-end h-4">
                  {[3,5,4,6,3].map((h,i) => (
                    <div key={i} className="w-1 bg-white/70 rounded-full animate-pulse" style={{ height: `${h*3}px`, animationDelay: `${i*100}ms` }} />
                  ))}
                </div>
                <span className="text-sm font-black text-white uppercase tracking-wide">Now Playing</span>
              </div>
              {playing.map(ann => {
                const tc = typeConf(ann.announcement_type, ann.type_label);
                return (
                  <div key={ann.id}>
                    <span className={`inline-flex items-center text-xs font-bold px-2 py-0.5 rounded-lg mb-1.5 bg-white/20 text-white`}>
                      {tc.label}
                    </span>
                    {ann.english_text && <p className="text-sm text-white font-medium leading-snug">{ann.english_text}</p>}
                    {ann.secondary_text && (
                      <p className="text-xs text-emerald-200 mt-1.5 flex items-center gap-1.5 italic">
                        <Globe size={10} /> {ann.secondary_text}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Scheduled Airport Announcements ── */}
          {scheduledAirportAnns && scheduledAirportAnns.length > 0 && (
            <div className="mb-4 rounded-2xl border border-blue-200 bg-blue-50 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-100 border-b border-blue-200">
                <Megaphone size={13} className="text-blue-600" />
                <span className="text-xs font-black text-blue-700 uppercase tracking-wide">Scheduled Airport Announcements</span>
                <span className="ml-auto text-xs font-bold px-2 py-0.5 rounded-full bg-blue-600 text-white">{scheduledAirportAnns.length}</span>
              </div>
              <div className="divide-y divide-blue-100">
                {scheduledAirportAnns.map(ann => {
                  const isImmediate = !ann.scheduled_at;
                  const isBroadcasting = ann.status === 'broadcasting';
                  const scheduledDate = ann.scheduled_at ? new Date(ann.scheduled_at) : null;
                  const timeLabel = scheduledDate
                    ? scheduledDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    : 'Immediate';
                  return (
                    <div key={ann.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-800 truncate">{ann.title}</p>
                          {ann.english_text && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{ann.english_text}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                          <StatusBadge
                            label={isBroadcasting ? 'Broadcasting' : ann.status === 'generating' ? 'Generating' : 'Scheduled'}
                            tone={ann.status === 'generating' && !isBroadcasting ? 'yellow' : 'info'}
                            pulse={isBroadcasting}
                            dot={isBroadcasting}
                          />
                          <span className="flex items-center gap-1 text-xs text-slate-500">
                            <Clock size={10} />
                            {timeLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Upcoming queue ── (nothing to add or change once the flight's session ended) */}
          {!sessionEnded && (
          <div className="mb-2">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-black text-slate-700 uppercase tracking-wide">Upcoming Queue</h2>
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                  {upcoming.length}
                </span>
              </div>
              <div className="flex items-center gap-3">
                {canInsert && (
                  <button onClick={() => setInsertForm({ insertAfterId: null })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-purple-600 text-white text-xs font-bold hover:bg-purple-700 transition-colors shadow-sm">
                    <Plus size={12} /> {canInsertDirect ? 'Ad-hoc' : 'Request ad-hoc'}
                  </button>
                )}
                {canReorder && (
                  <span className="hidden sm:flex text-xs text-slate-400 items-center gap-1">
                    <GripVertical size={12} /> Drag to reorder
                  </span>
                )}
              </div>
            </div>

            {pendingOther.length > 0 && (
              <div className="mb-3 space-y-2">
                {pendingOther.map(r => <PendingRequestCard key={`req-${r.id}`} req={r} reviewPath={reviewPath} compact />)}
              </div>
            )}

            {/* Shift-all bar */}
            <ShiftBar gateId={gateId} hasItems={upcoming.length > 0} onShifted={() => load(true)} />

            {upcoming.length === 0 && !insertForm && pendingTop.length === 0 ? (
              <div className="text-center py-14 bg-white rounded-2xl border border-dashed border-slate-200">
                <Mic2 size={36} className="text-slate-200 mx-auto mb-3" />
                <p className="text-slate-500 font-semibold">No upcoming announcements</p>
                <p className="text-slate-400 text-sm mt-1 mb-4">All announcements have been played or cleared.</p>
                {canInsert ? (
                  <button onClick={() => setInsertForm({ insertAfterId: null })}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-bold hover:bg-purple-700 transition-colors shadow-sm">
                    <Plus size={14} /> {canInsertDirect ? 'Add ad-hoc announcement' : 'Request ad-hoc announcement'}
                  </button>
                ) : (
                  <p className="text-slate-400 text-xs">Need an announcement played? Use Request Ad-Hoc — a supervisor or manager will approve it.</p>
                )}
              </div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter}
                onDragStart={handleDragStart} onDragEnd={handleDragEnd}>

                {canInsert && (
                  insertForm?.insertAfterId === null ? (
                    <InsertForm insertAfterId={null} positionLabel={positionLabel(null)} onSubmit={handleInsert}
                      onClose={() => setInsertForm(null)} loading={inserting}
                      mode={insertMode} templates={templates} flightCtx={flightCtx} />
                  ) : (
                    <TopInsertSlot form={insertForm} onOpen={(id) => setInsertForm({ insertAfterId: id })} />
                  )
                )}

                {pendingTop.map(r => <PendingRequestCard key={`req-${r.id}`} req={r} reviewPath={reviewPath} passed={r.insert_after_id != null} />)}

                <SortableContext items={upcoming.map(a => a.id)} strategy={verticalListSortingStrategy}>
                  <div className="flex flex-col gap-2">
                    {upcoming.map((ann, index) => (
                      <React.Fragment key={ann.id}>
                        <SortableAnnCard
                          ann={ann}
                          index={index}
                          gateId={gateId}
                          onCancel={handleCancel}
                          onSaved={() => load(true)}
                          insertForm={insertForm}
                          onInsertAfter={(id) => setInsertForm({ insertAfterId: id })}
                          canReorder={canReorder}
                          canInsert={canInsert}
                          isLast={index === upcoming.length - 1}
                          startsAt={index === 0 && ann.priority !== 'emergency' && session?.next_start_at ? Date.parse(session.next_start_at) : null}
                        />
                        {pendingAfter(ann.id).map(r => <PendingRequestCard key={`req-${r.id}`} req={r} reviewPath={reviewPath} />)}
                        {insertForm?.insertAfterId === ann.id && (
                          <InsertForm insertAfterId={ann.id} positionLabel={positionLabel(ann.id)} onSubmit={handleInsert}
                            onClose={() => setInsertForm(null)} loading={inserting}
                            mode={insertMode} templates={templates} flightCtx={flightCtx} />
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </SortableContext>

                <DragOverlay>
                  {activeAnn ? <DragCard ann={activeAnn} /> : null}
                </DragOverlay>
              </DndContext>
            )}
          </div>
          )}

          {/* ── History ── */}
          {history && history.length > 0 && (
            <div className="mt-5">
              <button onClick={() => setShowHistory(h => !h)}
                className="flex items-center gap-2 w-full px-3 py-2.5 rounded-2xl hover:bg-slate-50 transition-colors">
                <div className="flex-1 flex items-center gap-2">
                  <span className="text-xs font-black text-slate-500 uppercase tracking-wide">
                    Played History
                  </span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">
                    {history.length}
                  </span>
                </div>
                {showHistory ? <ChevronUp size={15} className="text-slate-400" /> : <ChevronDown size={15} className="text-slate-400" />}
              </button>

              {showHistory && (
                <div className="mt-2 flex flex-col gap-1.5 pl-2 border-l-2 border-slate-100 ml-3">
                  {history.map(ann => {
                    const sc = STATUS[ann.status] || STATUS.played;
                    const tc = typeConf(ann.announcement_type, ann.type_label);
                    return (
                      <div key={ann.id}
                        className="flex items-center gap-3 bg-white border border-slate-100 rounded-xl px-3 py-2.5 opacity-60">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${tc.dot}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-slate-600">{tc.label}</span>
                            <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} />
                            <span className="text-xs text-slate-400 ml-auto flex items-center gap-1">
                              <Clock size={10} /> {fmtTimeSec(ann.played_at || ann.scheduled_time)}
                            </span>
                          </div>
                          {ann.english_text && (
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{ann.english_text}</p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
